/**
 * AccessibilityHelper is a helper class for accessibility related things.
 * It has methods for marking elements as inert or interactive, as well
 * as methods for dealing with focus.

 * An inert element is not reachable by e.g. tabbing. This is useful when an
 * element is not visible but still in the DOM, where e.g. tabbing might end
 * up focusing an invisible element.
 *
 * (Inert here means that interactive elements get tabindex="-1". Real inertness,
 * e.g. with aria-hidden=true, would be better, but no browser implements that yet.)
 */

class AccessibilityHelper {
    static get interactiveElesSelector() {
        // This does not cover everything (currently missing sortable <th>s), extensive list
        // at http://www.whatwg.org/specs/web-apps/current-work/#interactive-content-0
        return `a, audio[controls], button, details, embed, iframe, img[usemap],
            input:not([type="hidden"]), keygen, label, object[usemap], select,
            textarea, video[controls], [tabindex]`;
    }

    /**
     * Make `element` inert.
     */
    static makeInert(element) {
        if (element.tabIndex > -1) {
            const prevIndex = element.hasAttribute('tabindex')
                ? element.tabIndex
                : '';
            element.setAttribute('data-previous-tabindex', prevIndex);
            element.tabIndex = -1;
        }
    }

    /**
     * Make an element that has previously been marked as inert by `makeInert()`
     * interactive.
     */
    static makeInteractive(element) {
        const prevIndex = element.getAttribute('data-previous-tabindex');
        if (prevIndex != null) {
            element.removeAttribute('data-previous-tabindex');
            element.removeAttribute('tabindex');
            if (prevIndex !== '') {
                element.tabIndex = prevIndex;
            }
        }
    }

    /**
     * Run `makeInert()` for `element` and its children.
     */
    static makeSubtreeInert(element) {
        [
            ...element.querySelectorAll(this.interactiveElesSelector),
            element,
        ].forEach(this.makeInert);
    }

    /**
     * Run `makeInteractive()` for `element` and its children.
     */
    static makeSubtreeInteractive(element) {
        [
            ...element.querySelectorAll('[data-previous-tabindex]'),
            element,
        ].forEach(this.makeInteractive);
    }

    /**
     * Focus `element`. If its not focusable, focus the first focusable child
     * element of `element`. Returns true if an element could be focused, false
     * otherwise.
     */
    static focusFirst(element) {
        return this.getFocusableElements(element).some(ele => {
            ele.focus();
            return document.activeElement === ele;
        });
    }

    /**
     * Get all focusable elements in `element`, including `element` itself, as an
     * array. `element` defaults to `document.body`.
     * Focusble elements here refers to elements that can be focused by keyboard.
     * Elements with negative tabindex can be focused programmatically, but they
     * will not included in the result of calling this method.
     * It will also exclude elements with a parent that has aria-hidden=true specified.
     */
    static getFocusableElements(element = document.body) {
        return [
            element,
            ...element.querySelectorAll(this.interactiveElesSelector),
        ]
            .filter(ele => ele.tabIndex > -1) // Only include elements that have a positive tabindex
            .filter(ele => !ele.matches("[aria-hidden='true'] *"));
    }
}

export default AccessibilityHelper;



// WEBPACK FOOTER //
// ./lib/AccessibilityHelper.js